import { apiClient } from "@/services/apiClient";

export type TenantInviteRole = "owner" | "admin" | "accountant" | "read_only";

export interface TenantMember {
  userId: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
  status: string;
  role: string;
  createdAt: string;
}

export interface TenantPendingInvite {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  invitedByUserId: string;
  invitedByEmail: string | null;
  createdAt: string;
  expired: boolean;
}

export function listTenantMembers(tenantId: string): Promise<TenantMember[]> {
  return apiClient
    .get<TenantMember[]>("/tenants/current/members", {
      params: { tenantId },
    })
    .then((res) => res.data);
}

export function listTenantPendingInvites(
  tenantId: string
): Promise<TenantPendingInvite[]> {
  return apiClient
    .get<TenantPendingInvite[]>("/auth/tenant-invites", {
      params: { tenantId },
    })
    .then((res) => res.data);
}

export function createTenantInvite(
  tenantId: string,
  payload: {
    email: string;
    role: TenantInviteRole;
    transferOwnership?: boolean;
  }
): Promise<void> {
  return apiClient
    .post("/auth/tenant-invite", {
      email: payload.email.trim().toLowerCase(),
      role: payload.role,
      tenantId,
      ...(payload.transferOwnership ? { transferOwnership: true } : {}),
    })
    .then(() => undefined);
}

export function resendTenantInvite(
  tenantId: string,
  inviteId: string
): Promise<void> {
  return apiClient
    .post(`/auth/tenant-invites/${inviteId}/resend`, {}, {
      params: { tenantId },
    })
    .then(() => undefined);
}

export function cancelTenantInvite(
  tenantId: string,
  inviteId: string
): Promise<void> {
  return apiClient
    .delete(`/auth/tenant-invites/${inviteId}`, {
      params: { tenantId },
    })
    .then(() => undefined);
}

export function updateMemberRole(
  _tenantId: string,
  userId: string,
  role: TenantInviteRole
): Promise<void> {
  return apiClient
    .patch(`/tenants/current/members/${userId}`, { role })
    .then(() => undefined);
}

export function removeMember(_tenantId: string, userId: string): Promise<void> {
  return apiClient
    .delete(`/tenants/current/members/${userId}`)
    .then(() => undefined);
}

export function updateMemberProfile(
  _tenantId: string,
  userId: string,
  payload: { firstName?: string | null; lastName?: string | null }
): Promise<void> {
  return apiClient
    .patch(`/tenants/current/members/${userId}/profile`, payload)
    .then(() => undefined);
}

export function setMemberStatus(
  _tenantId: string,
  userId: string,
  status: "active" | "disabled"
): Promise<void> {
  return apiClient
    .patch(`/tenants/current/members/${userId}/status`, { status })
    .then(() => undefined);
}

export function uploadMemberAvatar(
  _tenantId: string,
  userId: string,
  file: File
): Promise<{ profileImage: string }> {
  const formData = new FormData();
  formData.append("image", file);
  return apiClient
    .post<{ profileImage: string }>(
      `/tenants/current/members/${userId}/avatar`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    )
    .then((res) => res.data);
}
